import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { connect, Provider } from 'react-redux';
import { Dispatch, Store } from 'redux';

import { EInternetStatus, setInternetStatus, TInternetStatus } from './actions/internetStatus';
import { ChatsErrorPage } from './components/ChatsErrorPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MobileLayout } from './components/MobileLayout';
import { AlertContainer } from './containers/AlertContainer';
import { AuthNotificationContainer } from './containers/AuthNotificationContainer';
import { ChatActionModalContainer } from './containers/ChatActionModalContainer';
import { ChatListContainer } from './containers/ChatListContainer';
import { ChatWindowContainer } from './containers/ChatWindowContainer';
import { CustomActionModalContainer } from './containers/CustomActionModalContainer';
import { DailyrentReviewModalContainer } from './containers/DailyrentReviewModalContainer';
import { MasterTabsContainer } from './containers/MasterTabsContainer';
import { UserCardContainer } from './containers/UserCardContainer';
import { checkUsersSession } from './services/checkUsersSession';
import { setBlinkingTitleWithTimeout } from './services/setBlinkingTitle';
import { TAppState } from './types/appState';
import { IApplicationContext } from './types/applicationContext';
import { EMobileLayoutActiveSide } from './types/mobileSide';
import { TReduxActions } from './types/redux/actions';
import { ETelemetryEvents } from './types/telemetry';
import { EAgentAccountType } from './types/userData';
import { ApplicationContext } from './utils/applicationContext';
import { DailyrentReviewModalProvider } from './utils/dailyrent/useDailyrentReviewModal';
import { isSubAgentsChatsEnabled } from './utils/isSubAgentsChatsEnabled';

const style = require('./index.css');

interface IAppProps {
  activeSide: EMobileLayoutActiveSide;
  isAuthenticated: boolean;
  internetStatus: string;
  showErrorPage?: boolean;
  isMasterTabsEnabled: boolean;
  context: IApplicationContext;
}

interface IAppDispatchProps {
  setBlinkingTitle(isWindowFocused: boolean): void;
  setInternetStatus(status: TInternetStatus): void;
  checkUsersSession(): Promise<void>;
}

type TAppStateProps = Pick<IAppProps, 'activeSide'>;

type TAppProps = IAppProps & IAppDispatchProps;

class App extends React.Component<TAppProps, object> {
  public componentDidMount() {
    window.addEventListener('online', this.connectionCallback);
    window.addEventListener('offline', this.connectionCallback);
    window.addEventListener('focus', this.focusHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    const { context, showErrorPage } = this.props;

    if (showErrorPage) {
      if (context.logger) {
        context.logger.error('showErrorPage', {
          domain: 'src/shared/index.tsx',
          message: 'application started with error',
        });
      }

      if (context.telemetry) {
        context.telemetry.increment(ETelemetryEvents.ChatPageShowErrorPage);
      }
    }
  }

  public componentWillUnmount() {
    const { setBlinkingTitle } = this.props;
    // удаляет таймеры внутри мигающего заголовка
    setBlinkingTitle(true);
    window.removeEventListener('online', this.connectionCallback);
    window.removeEventListener('offline', this.connectionCallback);
    window.removeEventListener('focus', this.focusHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  private connectionCallback = async (event: Event) => {
    const { setInternetStatus, checkUsersSession } = this.props;

    setInternetStatus(event.type as TInternetStatus);

    if (event.type === EInternetStatus.Online) {
      await checkUsersSession();
    }
  };

  /**
   * TODO: решение временное, сообщать о том, что пользователь разлогинился должен бэкенд.
   * Каждый раз, когда происходит фокус окна, запрашиваем токен сессии.
   * Если вернулась ошибка, то предполагаем, что пользователь разлогинился.
   * Если оффлайн или показана страница ошибки, делаем запрос после изменения этих флагов
   */
  private focusHandler = async () => {
    const { internetStatus, showErrorPage, checkUsersSession } = this.props;

    if (internetStatus === EInternetStatus.Offline || showErrorPage) {
      return;
    } else {
      await checkUsersSession();
    }
  };

  private visibilityHandler = async () => {
    const { setBlinkingTitle } = this.props;

    const isWindowFocused = typeof document !== 'undefined' && document.visibilityState === 'visible';

    setBlinkingTitle(isWindowFocused);
  };

  private handeRetry = () => {
    window.location.reload();
  };

  public render() {
    const { showErrorPage, isAuthenticated, isMasterTabsEnabled } = this.props;

    if (showErrorPage) {
      return (
        <div className={style['chats_container']}>
          <ChatsErrorPage onRetry={this.handeRetry} />
        </div>
      );
    }

    return (
      <>
        <Responsive phone tablet>
          <div className={style['chats_container']}>
            <MobileLayout
              activeSide={this.props.activeSide}
              leftView={<ChatListContainer />}
              centerView={<ChatWindowContainer />}
              rightView={<UserCardContainer />}
            />
            <AlertContainer />
            <ChatActionModalContainer />
            <CustomActionModalContainer />
          </div>
        </Responsive>
        <Responsive desktop>
          <div className={style['chats_container']}>
            <div className={style['list']}>{isMasterTabsEnabled ? <MasterTabsContainer /> : <ChatListContainer />}</div>
            <div className={style['chat']}>
              {isAuthenticated ? <ChatWindowContainer /> : <AuthNotificationContainer />}
              <AlertContainer />
            </div>
            <UserCardContainer />
            <ChatActionModalContainer />
            <CustomActionModalContainer />
          </div>
        </Responsive>
        <DailyrentReviewModalContainer />
      </>
    );
  }
}

export const _AppContainer = connect<TAppStateProps, IAppDispatchProps, { context: IApplicationContext }, TAppProps>(
  (state: TAppState, props) => ({
    activeSide: state.mobileSide,
    showErrorPage: state.showErrorPage,
    isAuthenticated: state.user.isAuthenticated,
    internetStatus: state.internetStatus,
    isMasterTabsEnabled:
      state.user.isAuthenticated &&
      state.user.agentAccountType === EAgentAccountType.Agency &&
      isSubAgentsChatsEnabled(props.context.config),
  }),
  (dispatch: Dispatch<TReduxActions>) => ({
    setInternetStatus: (status: TInternetStatus) => dispatch(setInternetStatus(status)),
    checkUsersSession: () => dispatch<any>(checkUsersSession()),
    setBlinkingTitle: (isWindowFocused: boolean) => dispatch<any>(setBlinkingTitleWithTimeout({ isWindowFocused })),
  }),
  // tslint:disable-next-line:no-object-literal-type-assertion
  (stateProps: TAppStateProps, dispatchProps: IAppDispatchProps, ownProps: {}) =>
    ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
    } as TAppProps),
)(App);

export interface IApplicationProps {
  context: IApplicationContext;
  store: Store;
}

export const AppContainer: React.FC<IApplicationProps> = ({ store, context }) => {
  return (
    <ApplicationContext.Provider value={context}>
      <Provider store={store}>
        <DailyrentReviewModalProvider>
          <ErrorBoundary logger={context.logger} telemetry={context.telemetry}>
            <_AppContainer context={context} />
          </ErrorBoundary>
        </DailyrentReviewModalProvider>
      </Provider>
    </ApplicationContext.Provider>
  );
};
